import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Search, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { fmtLongDate, toIso, DEMARRAGE } from './recapUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const monthStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); return toIso(d); };

const preview = (r) => (r.activites || r.actions || r.rdv || '').replace(DEMARRAGE, '').replace(/\s+/g, ' ').trim().slice(0, 90) || '—';

export const RecapHistory = forwardRef(function RecapHistory({ userEmail, onOpen }, ref) {
  const [q, setQ] = useState('');
  const [du, setDu] = useState(monthStart());
  const [au, setAu] = useState(toIso(new Date()));
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });

  const load = useCallback((p = page) => {
    axios.get(`${API}/api/recaps`, { params: { user_email: userEmail, du: du || undefined, au: au || undefined, q: q || undefined, page: p, per_page: 6 } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], pages: 1, total: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, du, au, q, page]);

  useImperativeHandle(ref, () => ({ reload: () => load(page) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(page); }, [userEmail, page]);

  const search = () => { setPage(1); load(1); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" id="recap-history" data-testid="recap-history">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><CalendarDays className="h-5 w-5" /></div>
        <h2 className="text-lg font-bold text-slate-900">Historique des récapitulatifs</h2>
      </div>
      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Rechercher un récapitulatif..."
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="recap-search" />
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-slate-500">Du</span>
        <input type="date" value={du} onChange={(e) => setDu(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm" data-testid="recap-du" />
        <span className="text-sm text-slate-500">Au</span>
        <input type="date" value={au} onChange={(e) => setAu(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm" data-testid="recap-au" />
        <button onClick={search} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors" data-testid="recap-search-btn">Rechercher</button>
      </div>

      {data.items.length === 0 ? (
        <p className="text-center py-8 text-sm text-slate-500" data-testid="recap-empty">Aucun récapitulatif sur cette période.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50 transition-colors" data-testid={`recap-item-${r.date}`}>
              <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0"><FileText className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">{fmtLongDate(r.date)}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{preview(r)}…</p>
              </div>
              <button onClick={() => onOpen(r.date)} className="h-8 px-4 rounded-md border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50 transition-colors" data-testid={`recap-view-${r.date}`}>Voir</button>
            </div>
          ))}
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-4" data-testid="recap-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          {Array.from({ length: data.pages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map((p) => (
            <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-md text-sm font-semibold ${p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>{p}</button>
          ))}
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
});
